import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Image as ImageIcon, Upload, RotateCcw, Plus, Pencil, Trash2, Loader2, X, Eye, EyeOff, MousePointerClick, Timer } from "lucide-react";
import { Shell } from "@/components/Shell";
import { api, apiError } from "@/lib/api";
import { mediaUrl } from "@/lib/media";
import { PopupCard } from "@/components/SitePopup";
import { HERO_DEFAULT_IMG } from "@/pages/Accueil";

const ACCEPT = "image/jpeg,image/png,image/webp";
const MAX_BYTES = 5 * 1024 * 1024;
const PAGE_LABELS = { home: "Accueil", reserver: "Réserver", all: "Toutes les pages" };
const input = "mt-1 w-full rounded-lg border border-neutral-300 px-3 py-2.5 text-sm outline-none focus:border-brand";

const checkFile = (f) => {
  if (!f) return false;
  if (!ACCEPT.split(",").includes(f.type)) { toast.error("Format non accepté : JPEG, PNG ou WebP."); return false; }
  if (f.size > MAX_BYTES) { toast.error("Image trop lourde (5 Mo maximum)."); return false; }
  return true;
};

const statusOf = (p) => {
  const d = new Date();
  const today = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  if (!p.active) return ["Hors ligne", "bg-neutral-100 text-neutral-500"];
  if (p.start_date && p.start_date > today) return ["Programmée", "bg-sky-50 text-sky-700"];
  if (p.end_date && p.end_date < today) return ["Terminée", "bg-amber-50 text-amber-700"];
  return ["En ligne", "bg-emerald-50 text-emerald-700"];
};

function HeroImageCard() {
  const [url, setUrl] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get("/admin/site").then(({ data }) => setUrl(data.hero_image_url || "")).catch(() => setUrl(""));
  }, []);

  const upload = async (f) => {
    if (!checkFile(f)) return;
    const fd = new FormData();
    fd.append("file", f);
    setBusy(true);
    try {
      const { data } = await api.post("/admin/site/hero-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setUrl(data.hero_image_url);
      toast.success("Image du hero mise à jour sur le site.");
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const reset = async () => {
    if (!window.confirm("Revenir à l'image par défaut ?")) return;
    setBusy(true);
    try {
      await api.delete("/admin/site/hero-image");
      setUrl("");
      toast.success("Image par défaut rétablie.");
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const custom = !!url;
  return (
    <section className="rounded-2xl border border-neutral-200 bg-white p-6" data-testid="hero-image-card">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="flex items-center gap-2 text-lg font-extrabold"><ImageIcon className="h-5 w-5 text-brand" /> {"Image du hero (page d'accueil)"}</h2>
          <p className="mt-1 text-sm text-neutral-500">{"JPEG, PNG ou WebP · 5 Mo max · idéalement 1000 × 1200 px (portrait) ou plus. Visible sur ordinateur et tablette."}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={() => fileRef.current?.click()} disabled={busy} data-testid="hero-upload-btn"
            className="inline-flex items-center gap-2 rounded-lg bg-brand px-4 py-2.5 text-sm font-bold text-white hover:bg-brand-hover disabled:opacity-60">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Changer l'image
          </button>
          {custom && (
            <button type="button" onClick={reset} disabled={busy} data-testid="hero-reset-btn"
              className="inline-flex items-center gap-2 rounded-lg border border-neutral-300 px-4 py-2.5 text-sm font-bold text-neutral-700 hover:bg-neutral-50 disabled:opacity-60">
              <RotateCcw className="h-4 w-4" /> {"Revenir à l'image par défaut"}
            </button>
          )}
          <input ref={fileRef} type="file" accept={ACCEPT} className="hidden" data-testid="hero-file-input"
            onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; upload(f); }} />
        </div>
      </div>
      <div className="mt-5 grid gap-4 sm:grid-cols-[280px_1fr] sm:items-center">
        <div className="overflow-hidden rounded-2xl bg-neutral-100 ring-1 ring-neutral-200">
          {url === null ? (
            <div className="grid aspect-[5/6] place-items-center"><Loader2 className="h-5 w-5 animate-spin text-neutral-400" /></div>
          ) : (
            <img src={custom ? mediaUrl(url) : HERO_DEFAULT_IMG} alt="Aperçu du hero" className="aspect-[5/6] w-full object-cover" data-testid="hero-preview" />
          )}
        </div>
        <p className="text-sm text-neutral-600" data-testid="hero-image-status">
          {custom ? "✅ Image personnalisée en ligne." : "Image par défaut actuellement affichée."}
        </p>
      </div>
    </section>
  );
}

const EMPTY = {
  title: "", text: "", image_url: "", cta_label: "", cta_url: "/reserver", active: true,
  start_date: "", end_date: "", trigger: "delay", delay_seconds: 8, pages: ["home"], priority: 0,
};

function PopupFormModal({ initial, onClose, onSaved }) {
  const editing = !!initial?.id;
  const [form, setForm] = useState(() => ({ ...EMPTY, ...(initial || {}), start_date: initial?.start_date || "", end_date: initial?.end_date || "" }));
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const togglePage = (k) => {
    setForm((f) => {
      if (k === "all") return { ...f, pages: f.pages.includes("all") ? ["home"] : ["all"] };
      const base = f.pages.filter((x) => x !== "all");
      const pages = base.includes(k) ? base.filter((x) => x !== k) : [...base, k];
      return { ...f, pages };
    });
  };

  const uploadImage = async (f) => {
    if (!checkFile(f)) return;
    const fd = new FormData();
    fd.append("file", f);
    setUploading(true);
    try {
      const { data } = await api.post("/admin/media", fd, { headers: { "Content-Type": "multipart/form-data" } });
      upd("image_url", data.url);
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || e.message); }
    finally { setUploading(false); }
  };

  const save = async () => {
    if (!form.title.trim()) { toast.error("Le titre est obligatoire."); return; }
    if (!form.pages.length) { toast.error("Choisis au moins une page."); return; }
    const payload = {
      title: form.title, text: form.text, image_url: form.image_url, cta_label: form.cta_label, cta_url: form.cta_url,
      active: form.active, start_date: form.start_date || null, end_date: form.end_date || null,
      trigger: form.trigger, delay_seconds: parseInt(form.delay_seconds, 10) || 0, pages: form.pages,
      priority: parseInt(form.priority, 10) || 0,
    };
    setSaving(true);
    try {
      if (editing) await api.put(`/admin/popups/${initial.id}`, payload);
      else await api.post("/admin/popups", payload);
      toast.success(editing ? "Pop-up mise à jour." : "Pop-up créée.");
      onSaved();
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || e.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose} data-testid="popup-form-modal">
      <div className="max-h-[94vh] w-full max-w-5xl overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-extrabold">{editing ? "Modifier la pop-up" : "Nouvelle pop-up"}</h3>
          <button onClick={onClose} className="rounded-full p-1 text-neutral-400 hover:bg-neutral-100"><X className="h-5 w-5" /></button>
        </div>
        <div className="mt-5 grid gap-6 lg:grid-cols-[1fr_380px]">
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="block sm:col-span-2"><span className="text-xs font-semibold text-neutral-500">Titre *</span>
              <input value={form.title} maxLength={120} onChange={(e) => upd("title", e.target.value)} placeholder="Ex. -100 € sur le permis accéléré à Paris" data-testid="popup-title" className={input} />
            </label>
            <label className="block sm:col-span-2"><span className="text-xs font-semibold text-neutral-500">Texte</span>
              <textarea rows={3} maxLength={600} value={form.text} onChange={(e) => upd("text", e.target.value)} placeholder="Ex. Offre valable jusqu'au 31 octobre pour les 10 premiers inscrits." data-testid="popup-text" className={input} />
            </label>
            <div className="sm:col-span-2">
              <span className="text-xs font-semibold text-neutral-500">Image (facultative)</span>
              <div className="mt-1 flex flex-wrap items-center gap-2">
                <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="popup-image-btn"
                  className="inline-flex items-center gap-2 rounded-lg border border-neutral-300 px-3 py-2 text-sm font-semibold text-neutral-700 hover:bg-neutral-50 disabled:opacity-60">
                  {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} {form.image_url ? "Changer l'image" : "Ajouter une image"}
                </button>
                {form.image_url && (
                  <button type="button" onClick={() => upd("image_url", "")} data-testid="popup-image-remove" className="text-sm font-semibold text-red-600 hover:underline">{"Retirer l'image"}</button>
                )}
                <input ref={fileRef} type="file" accept={ACCEPT} className="hidden" data-testid="popup-image-input"
                  onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; uploadImage(f); }} />
              </div>
            </div>
            <label className="block"><span className="text-xs font-semibold text-neutral-500">Texte du bouton</span>
              <input value={form.cta_label} maxLength={60} onChange={(e) => upd("cta_label", e.target.value)} placeholder="J'en profite" data-testid="popup-cta-label" className={input} />
            </label>
            <label className="block"><span className="text-xs font-semibold text-neutral-500">Lien du bouton</span>
              <input value={form.cta_url} onChange={(e) => upd("cta_url", e.target.value)} placeholder="/reserver?ville=Paris" data-testid="popup-cta-url" className={input} />
              <span className="mt-1 block text-[11px] text-neutral-400">{"Page du site (/reserver?ville=Paris) ou lien https://"}</span>
            </label>

            <div className="sm:col-span-2">
              <span className="text-xs font-semibold text-neutral-500">Pages</span>
              <div className="mt-1 flex flex-wrap gap-2" data-testid="popup-pages">
                {Object.entries(PAGE_LABELS).map(([k, l]) => {
                  const on = form.pages.includes(k);
                  return (
                    <button key={k} type="button" onClick={() => togglePage(k)} data-testid={`popup-page-${k}`}
                      className={`rounded-lg border px-3 py-2 text-sm font-bold ${on ? "border-brand bg-brand/10 text-brand" : "border-neutral-300 text-neutral-600"}`}>{l}</button>
                  );
                })}
              </div>
            </div>

            <div className="sm:col-span-2">
              <span className="text-xs font-semibold text-neutral-500">Déclenchement</span>
              <div className="mt-1 flex flex-wrap items-center gap-2" data-testid="popup-trigger">
                <button type="button" onClick={() => upd("trigger", "delay")} data-testid="popup-trigger-delay"
                  className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-2 text-sm font-bold ${form.trigger === "delay" ? "border-brand bg-brand/10 text-brand" : "border-neutral-300 text-neutral-600"}`}><Timer className="h-4 w-4" /> Après un délai</button>
                <button type="button" onClick={() => upd("trigger", "exit")} data-testid="popup-trigger-exit"
                  className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-2 text-sm font-bold ${form.trigger === "exit" ? "border-brand bg-brand/10 text-brand" : "border-neutral-300 text-neutral-600"}`}><MousePointerClick className="h-4 w-4" /> Quand il veut partir</button>
                {form.trigger === "delay" && (
                  <span className="inline-flex items-center gap-2 text-sm text-neutral-600">
                    <input type="number" min="0" max="300" value={form.delay_seconds} onChange={(e) => upd("delay_seconds", e.target.value)} data-testid="popup-delay" className="w-20 rounded-lg border border-neutral-300 px-2 py-2 text-right text-sm" /> secondes
                  </span>
                )}
              </div>
              {form.trigger === "exit" && <p className="mt-1 text-[11px] text-neutral-400">{"Sur ordinateur : quand la souris sort par le haut de la page. Sur mobile : après 20 secondes."}</p>}
            </div>

            <label className="block"><span className="text-xs font-semibold text-neutral-500">Début (facultatif)</span>
              <input type="date" value={form.start_date} onChange={(e) => upd("start_date", e.target.value)} data-testid="popup-start" className={input} />
            </label>
            <label className="block"><span className="text-xs font-semibold text-neutral-500">Fin (facultatif)</span>
              <input type="date" value={form.end_date} onChange={(e) => upd("end_date", e.target.value)} data-testid="popup-end" className={input} />
            </label>
            <label className="block"><span className="text-xs font-semibold text-neutral-500">Priorité (0-100, la plus haute passe en premier)</span>
              <input type="number" min="0" max="100" value={form.priority} onChange={(e) => upd("priority", e.target.value)} data-testid="popup-priority" className={input} />
            </label>
            <label className="flex items-center gap-2 self-end pb-2 text-sm font-semibold text-neutral-700">
              <input type="checkbox" checked={form.active} onChange={(e) => upd("active", e.target.checked)} data-testid="popup-active" className="h-4 w-4 rounded border-neutral-300 text-brand" /> En ligne
            </label>
          </div>

          <div>
            <p className="text-xs font-bold uppercase tracking-wider text-neutral-400">Aperçu</p>
            <div className="mt-2 grid place-items-center rounded-2xl bg-neutral-800/80 p-5" data-testid="popup-preview">
              <PopupCard popup={form} preview onClose={() => {}} onCta={() => {}} />
            </div>
          </div>
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <button onClick={onClose} className="rounded-lg border border-neutral-300 px-4 py-2 text-sm font-semibold">Annuler</button>
          <button onClick={save} disabled={saving || uploading} data-testid="popup-save"
            className="inline-flex items-center gap-2 rounded-lg bg-brand px-4 py-2 text-sm font-bold text-white hover:bg-brand-hover disabled:opacity-60">
            {saving && <Loader2 className="h-4 w-4 animate-spin" />} {editing ? "Enregistrer" : "Créer la pop-up"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AdminSite() {
  const [popups, setPopups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/popups"); setPopups(data || []); }
    catch (e) { toast.error(apiError(e.response?.data?.detail) || e.message); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggleActive = async (p) => {
    const { id, created_at, updated_at, ...rest } = p;
    try {
      await api.put(`/admin/popups/${id}`, { ...rest, active: !p.active });
      toast.success(p.active ? "Pop-up mise hors ligne." : "Pop-up remise en ligne.");
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail) || e.message); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Supprimer la pop-up « ${p.title} » ?`)) return;
    try { await api.delete(`/admin/popups/${p.id}`); toast.success("Pop-up supprimée."); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail) || e.message); }
  };

  return (
    <Shell role="admin">
      <div className="px-5 py-8 sm:px-8">
        <h1 className="text-3xl font-extrabold tracking-tighter">Site & pop-ups</h1>
        <p className="mt-2 text-neutral-500">{"Change l'image d'accueil et mets en avant tes offres avec des pop-ups."}</p>

        <div className="mt-6"><HeroImageCard /></div>

        <section className="mt-6 rounded-2xl border border-neutral-200 bg-white p-6" data-testid="popups-card">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <h2 className="text-lg font-extrabold">Pop-ups offres</h2>
              <p className="mt-1 text-sm text-neutral-500">{"Une seule pop-up s'affiche à la fois (la plus prioritaire), au maximum une fois par jour et par visiteur. Jamais sur l'admin ni l'espace auto-école."}</p>
            </div>
            <button onClick={() => setEditing({})} data-testid="popup-add-btn"
              className="inline-flex items-center gap-2 rounded-lg bg-brand px-4 py-2.5 text-sm font-bold text-white hover:bg-brand-hover">
              <Plus className="h-4 w-4" /> Nouvelle pop-up
            </button>
          </div>

          {loading ? (
            <div className="grid place-items-center py-10"><Loader2 className="h-5 w-5 animate-spin text-brand" /></div>
          ) : popups.length === 0 ? (
            <p className="mt-6 rounded-xl border border-dashed border-neutral-300 p-8 text-center text-sm text-neutral-500" data-testid="popups-empty">
              {"Aucune pop-up pour l'instant. Crée ta première offre !"}
            </p>
          ) : (
            <ul className="mt-5 divide-y divide-neutral-100" data-testid="popups-list">
              {popups.map((p) => {
                const [label, cls] = statusOf(p);
                return (
                  <li key={p.id} className="flex flex-wrap items-center justify-between gap-3 py-4" data-testid={`popup-row-${p.id}`}>
                    <div className="flex min-w-0 items-center gap-3">
                      {p.image_url ? <img src={mediaUrl(p.image_url)} alt="" className="h-12 w-16 shrink-0 rounded-lg object-cover" />
                        : <span className="grid h-12 w-16 shrink-0 place-items-center rounded-lg bg-neutral-100 text-neutral-400"><ImageIcon className="h-5 w-5" /></span>}
                      <div className="min-w-0">
                        <p className="truncate font-bold text-neutral-900">{p.title}</p>
                        <p className="mt-0.5 text-xs text-neutral-500">
                          {(p.pages || []).map((k) => PAGE_LABELS[k] || k).join(", ")}
                          {" · "}{p.trigger === "exit" ? "quand il veut partir" : `après ${p.delay_seconds} s`}
                          {p.start_date || p.end_date ? ` · ${p.start_date || "…"} → ${p.end_date || "…"}` : ""}
                          {" · priorité "}{p.priority}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`rounded-full px-2.5 py-1 text-xs font-bold ${cls}`} data-testid={`popup-status-${p.id}`}>{label}</span>
                      <button onClick={() => toggleActive(p)} title={p.active ? "Mettre hors ligne" : "Mettre en ligne"} data-testid={`popup-toggle-${p.id}`}
                        className="rounded-md border border-neutral-300 p-2 text-neutral-600 hover:bg-neutral-100">{p.active ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}</button>
                      <button onClick={() => setEditing(p)} title="Modifier" data-testid={`popup-edit-${p.id}`}
                        className="rounded-md border border-neutral-300 p-2 text-neutral-600 hover:bg-neutral-100"><Pencil className="h-4 w-4" /></button>
                      <button onClick={() => remove(p)} title="Supprimer" data-testid={`popup-delete-${p.id}`}
                        className="rounded-md border border-red-200 p-2 text-red-600 hover:bg-red-50"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      </div>
      {editing && <PopupFormModal initial={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </Shell>
  );
}
